/**
 * Surveillance network health envelope (per spec §18, §30.13).
 */
import { z } from 'zod';

// Canonical surveillance service IDs per ticket 86e1fvf06. Lowercase to
// match the broker topic suffix (surveillance_traffic/<id> +
// surveillance_sensors/<id>); single source of truth for both the
// allowlist (what we subscribe to) and the UI labels.
export const ServiceId = z.enum([
  'live_ads',
  'live_vision',
  'live_mlat',
  'live_drone_rf',
  'live_regional_edmonton',
  'live_regional_calgary',
  'live_regional_fort_mcmurray',
  // Not a broker emitter — synthesised on the client by the Live_Fused
  // control panel while a fusion runs. Excluded from ALLOWED_EMITTERS below.
  'live_fused',
]);
export type ServiceId = z.infer<typeof ServiceId>;

/** The services we have curated names, colours, trust and ordering for. Not a
 *  gate any more (see `isServiceEmitter`) — it seeds the cold-start list, fixes
 *  the display order, and is subscribed up front so their traffic flows from the
 *  first second. Anything else the broker reports is discovered at runtime.
 *
 *  Deliberately not `ServiceId.options`: subscribing to `live_fused` would be
 *  a topic that does not exist. */
export const KNOWN_EMITTERS: readonly ServiceId[] = [
  'live_ads',
  'live_vision',
  'live_mlat',
  'live_drone_rf',
  'live_regional_edmonton',
  'live_regional_calgary',
  'live_regional_fort_mcmurray',
];

/** Emitter-shaped names on the surveillance topics that are NOT services — the
 *  ticket 86e1fvf06 "DO NOT ADD" list. They are also the high-rate ones
 *  (`live_ads_count` ≈ 400 msg/s, `stream` ≈ 360 msg/s, `live_community`), which
 *  is why discovery listens on the sensors topics only and subscribes to a
 *  service's traffic topic once the service has been seen. `live_fused` is
 *  synthesised on the client. */
export const EXCLUDED_EMITTERS: ReadonlySet<string> = new Set([
  'live_community',
  'live_ads_count',
  'metrics',
  'stream',
  'live_fused',
]);

const SERVICE_EMITTER_ID = /^live_[a-z0-9_]+$/;

/** True for an emitter name that is a real surveillance service: `live_…` and
 *  not on the exclusion list. This is the one gate (ENG: services follow the
 *  broker) — replaces the old fixed allowlist. */
export function isServiceEmitter(emitter: string): boolean {
  return SERVICE_EMITTER_ID.test(emitter) && !EXCLUDED_EMITTERS.has(emitter);
}

/** Known services the broker is expected to report on the surveillance topics,
 *  shown as "Connecting…" while the cold-start window runs so the list is not
 *  empty for up to 90 s. `live_drone_rf` is left out: RF drones arrive on
 *  `v1/ingest/live_rf/*`, and no `surveillance_*` topic exists for it. */
export const PLACEHOLDER_EMITTERS: readonly ServiceId[] = KNOWN_EMITTERS.filter(
  (id) => id !== 'live_drone_rf',
);

/** Display label per ticket. Used by the pill row, service header, and
 *  any list that renders a service id. */
export const SERVICE_LABEL: Record<ServiceId, string> = {
  live_ads:                     'Live ADS',
  live_vision:                  'Live Vision',
  live_mlat:                    'Live MLAT',
  live_drone_rf:                'Live Drone',
  live_regional_edmonton:       'Live_c Edmonton',
  live_regional_calgary:        'Live_c Calgary',
  live_regional_fort_mcmurray:  'Live_c Fort McMurray',
  live_fused:                   'Live_Fused',
};

const titleWord = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

/** Display label for any service id. Curated names win; an id nobody has named
 *  yet reads sensibly instead of as a raw topic suffix — `live_regional_ottawa`
 *  → "Live_c Ottawa" (matching the other regionals), `live_foo_bar` → "Live Foo Bar". */
export function serviceLabel(id: string): string {
  const known = (SERVICE_LABEL as Record<string, string>)[id];
  if (known) return known;
  const words = id.replace(/^live_/, '').split('_').filter(Boolean);
  if (words[0] === 'regional' && words.length > 1) {
    return `Live_c ${words.slice(1).map(titleWord).join(' ')}`;
  }
  return `Live ${words.map(titleWord).join(' ')}`.trim();
}

/** Display order: the curated services first, in their fixed order, then every
 *  other service A→Z so the list does not shuffle as new ones appear. */
export function orderServices<T extends string>(ids: Iterable<T>): T[] {
  const all = [...new Set(ids)];
  const rank = (id: string) => (KNOWN_EMITTERS as readonly string[]).indexOf(id);
  return all.sort((x, y) => {
    const rx = rank(x);
    const ry = rank(y);
    if (rx >= 0 && ry >= 0) return rx - ry;
    if (rx >= 0) return -1;
    if (ry >= 0) return 1;
    return x.localeCompare(y);
  });
}

export const SensorState = z.enum(['live', 'degraded', 'offline']);

export const SensorStation = z.object({
  sensor_id: z.string(),
  label: z.string(),
  lat: z.number(),
  lng: z.number(),
  state: SensorState,
  rtt_ms: z.number().nullable(),
  latency_ms: z.number().nullable(),
  rssi: z.number().nullable(),
  target_count: z.number(),
  last_seen: z.string().datetime(),
});

// Mockup `coverageModels[]` — each model is one AGL slice with a
// list of contributing sensors and a polygon ring of [lng, lat] points.
// The polygon is the coverage footprint at that AGL; sensors that go
// offline make the model "not accurate" (needs regen).
export const CoverageModel = z.object({
  agl: z.number(), // metres or feet — interpreted in UI
  sensors: z.array(z.string()), // sensor_ids contributing
  createdAt: z.string(),
  // GeoJSON-style ring: [[lng, lat], …]. Closed (first == last) optional.
  polygon: z.array(z.tuple([z.number(), z.number()])),
});

export const SurveillanceService = z.object({
  // Any service the broker reports (see `isServiceEmitter`), not only the curated
  // `ServiceId` names — the list follows the broker.
  service_id: z.string(),
  label: z.string(),
  type: z.enum(['Cooperative', 'Non-Cooperative']),
  // `Connecting` — initial state for services that haven't received
  // their first envelope from the broker yet (within the cold-start
  // window defined by SURVEILLANCE_CONNECTING_MS in MqttSourceLegacy).
  // After the window expires without data, the service flips to
  // `Offline`. Once any envelope lands, station-based derivation takes
  // over (Active / Degraded / Offline).
  service_state: z.enum(['Connecting', 'Active', 'Degraded', 'Offline']),
  trust: z.enum(['High', 'Medium', 'Low']),
  online_sensors: z.number(),
  total_sensors: z.number(),
  avg_latency_ms: z.number().nullable(),
  stations: z.array(SensorStation),
  // Mockup `SURV_DATA[net].visibleTargets` — list of target_ids this
  // service can see. Used by Diagnostic mode to filter aircraft markers
  // when a single network is selected.
  visibleTargets: z.array(z.string()).default([]),
  // Mockup `SURV_DATA[net].coverageModels`
  coverageModels: z.array(CoverageModel).default([]),
});

export const SurveillanceEnvelope = z.object({
  services: z.array(SurveillanceService),
  received_at: z.string().datetime(),
});

export type SurveillanceEnvelope = z.infer<typeof SurveillanceEnvelope>;
export type SurveillanceService = z.infer<typeof SurveillanceService>;
export type SensorStation = z.infer<typeof SensorStation>;
export type CoverageModel = z.infer<typeof CoverageModel>;
